import { FC } from 'react';
import { GRNPayment } from 'src/models/Management.interface';
import { GRNPaymentStatus } from 'src/models/enums/GRNStatus.enum';
import { PaymentMode } from 'src/models/enums/PaymentMode.enum';
import Status from 'src/components/Status';
import { DATE_FORMATS } from 'src/utils/properties.utils';
import { formatCurrency } from 'src/utils/format.utils';
import {
  getPaymentModeText,
  getPaymentStatusText,
  getChequeStatusText,
} from 'src/utils/status.utils';
import {
  LabelTypography,
  ValueTypography,
  StatusInfoBox,
  DetailLabelText,
  DetailValueText,
  HeaderLeft,
  FixedHeaderWrapper,
  HeaderSection,
  ViewStyledDialog,
  ViewCloseIcon,
  ContentWrapper,
  MainContentGrid,
  PaymentInfoRow,
  PaymentDetailsBox,
  InfoBlock,
  InfoBlockRight,
  PaymentMethodCard,
  PaymentMethodRow,
  PaymentMethodDivider,
  StatusInfoRow,
  RejectionNoteBox,
  RejectionLabelTypography,
  SmallValueTypography,
  NoteText,
  TitleTypographyBlue,
} from './GRNPaymentDialog.styles';

interface ViewGRNPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  payment: GRNPayment | null;
}

const ViewGRNPaymentDialog: FC<ViewGRNPaymentDialogProps> = ({
  open,
  onClose,
  payment,
}) => {
  if (!payment) return null;

  const isCheque = payment.paymentMode === PaymentMode.CHEQUE;
  const isOnline = payment.paymentMode === PaymentMode.ONLINE;
  const isRejected = payment.status === GRNPaymentStatus.REJECTED;
  const isApproved = payment.status === GRNPaymentStatus.APPROVED;

  return (
    <ViewStyledDialog open={open} onClose={onClose}>
      <FixedHeaderWrapper>
        <HeaderSection>
          <HeaderLeft>
            <TitleTypographyBlue>GRN Payment Details</TitleTypographyBlue>
            <Status status={getPaymentStatusText(payment.status)} />
          </HeaderLeft>
          <ViewCloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={onClose}
          />
        </HeaderSection>
      </FixedHeaderWrapper>
      <ContentWrapper>
        <MainContentGrid>
          {/* Left Section - Payment Details */}
          <PaymentDetailsBox>
            {/* Date and Amount Row */}
            <PaymentInfoRow>
              <InfoBlock>
                <LabelTypography>Date</LabelTypography>
                <ValueTypography>
                  {new Date(payment.paymentDate).toLocaleDateString(
                    DATE_FORMATS.LOCALE
                  )}
                </ValueTypography>
              </InfoBlock>
              <InfoBlockRight>
                <LabelTypography>Amount (LKR)</LabelTypography>
                <ValueTypography>
                  {formatCurrency(payment.amount)}
                </ValueTypography>
              </InfoBlockRight>
            </PaymentInfoRow>

            {/* Payment Mode Row */}
            <PaymentInfoRow>
              <InfoBlock>
                <LabelTypography>Payment Mode</LabelTypography>
                <ValueTypography>
                  {typeof payment.paymentMode === 'string'
                    ? payment.paymentMode
                    : getPaymentModeText(payment.paymentMode)}
                </ValueTypography>
              </InfoBlock>
            </PaymentInfoRow>

            {/* Cheque Details Card */}
            {isCheque && (
              <PaymentMethodCard>
                <PaymentMethodRow>
                  <DetailLabelText>Cheque Date</DetailLabelText>
                  <DetailValueText>
                    {payment.chequeDate
                      ? new Date(payment.chequeDate).toLocaleDateString(
                          DATE_FORMATS.LOCALE
                        )
                      : '-'}
                  </DetailValueText>
                </PaymentMethodRow>
                <PaymentMethodDivider />
                <PaymentMethodRow>
                  <DetailLabelText>Cheque Number</DetailLabelText>
                  <DetailValueText>
                    {payment.chequeNumber || '-'}
                  </DetailValueText>
                </PaymentMethodRow>
                <PaymentMethodDivider />
                <PaymentMethodRow>
                  <DetailLabelText>Cheque Status</DetailLabelText>
                  <DetailValueText>
                    {getChequeStatusText(payment.chequeStatus)}
                  </DetailValueText>
                </PaymentMethodRow>
                {payment.chequeRealizedDate && (
                  <>
                    <PaymentMethodDivider />
                    <PaymentMethodRow>
                      <DetailLabelText>Cheque Realized Date</DetailLabelText>
                      <DetailValueText>
                        {new Date(
                          payment.chequeRealizedDate
                        ).toLocaleDateString(DATE_FORMATS.LOCALE)}
                      </DetailValueText>
                    </PaymentMethodRow>
                  </>
                )}
                <PaymentMethodDivider />
                <PaymentMethodRow>
                  <DetailLabelText>Bank Account</DetailLabelText>
                  <DetailValueText>
                    {payment.bankName && payment.accountNumber
                      ? `${payment.bankName} - ${payment.accountNumber}`
                      : payment.accountNumber || '-'}
                  </DetailValueText>
                </PaymentMethodRow>
              </PaymentMethodCard>
            )}

            {/* Online Payment Details Card */}
            {isOnline && (
              <PaymentMethodCard>
                <PaymentMethodRow>
                  <DetailLabelText>Reference Number</DetailLabelText>
                  <DetailValueText>
                    {payment.transactionReference || '-'}
                  </DetailValueText>
                </PaymentMethodRow>
                <PaymentMethodDivider />
                <PaymentMethodRow>
                  <DetailLabelText>Bank Account</DetailLabelText>
                  <DetailValueText>
                    {payment.bankName && payment.accountNumber
                      ? `${payment.bankName} - ${payment.accountNumber}`
                      : payment.accountNumber || '-'}
                  </DetailValueText>
                </PaymentMethodRow>
              </PaymentMethodCard>
            )}
          </PaymentDetailsBox>

          {/* Right Section - Status Info */}
          {isRejected && (
            <StatusInfoBox>
              <StatusInfoRow>
                <InfoBlock>
                  <LabelTypography>Rejected Date</LabelTypography>
                  <SmallValueTypography>
                    {payment.approvedDate
                      ? new Date(payment.approvedDate).toLocaleDateString(
                          DATE_FORMATS.LOCALE
                        )
                      : '-'}
                  </SmallValueTypography>
                </InfoBlock>
                <InfoBlockRight>
                  <LabelTypography>Rejected User</LabelTypography>
                  <SmallValueTypography>
                    {payment.approvedBy || '-'}
                  </SmallValueTypography>
                </InfoBlockRight>
              </StatusInfoRow>
              <RejectionNoteBox>
                <RejectionLabelTypography>
                  Rejection Note
                </RejectionLabelTypography>
                <NoteText>{payment.notes || '-'}</NoteText>
              </RejectionNoteBox>
            </StatusInfoBox>
          )}

          {isApproved && (
            <StatusInfoBox>
              <StatusInfoRow>
                <InfoBlock>
                  <LabelTypography>Approved Date</LabelTypography>
                  <SmallValueTypography>
                    {payment.approvedDate
                      ? new Date(payment.approvedDate).toLocaleDateString(
                          DATE_FORMATS.LOCALE
                        )
                      : '-'}
                  </SmallValueTypography>
                </InfoBlock>
                <InfoBlockRight>
                  <LabelTypography>Approved By</LabelTypography>
                  <SmallValueTypography>
                    {payment.approvedBy || '-'}
                  </SmallValueTypography>
                </InfoBlockRight>
              </StatusInfoRow>
            </StatusInfoBox>
          )}
        </MainContentGrid>
      </ContentWrapper>
    </ViewStyledDialog>
  );
};

export default ViewGRNPaymentDialog;
